import { destroy, post } from "@rails/request.js"
import { useState } from "react"

import { ConfirmDeleteModal } from "../shared/ConfirmDeleteModal"
import { Modal } from "../shared/Modal"
import type {
  StayHandoverCategory,
  StayHandoverNote,
  StayPlaybookTemplateKey,
  StayPlaybookTemplates,
} from "./stayData"

type StayNotesProps = {
  finalNotes: readonly StayHandoverNote[]
  notes: readonly StayHandoverNote[]
  playbookTemplates: StayPlaybookTemplates
  stayId: string
}

type ModalMode = "final" | "stay"

const noteCategoryOptions = [
  { value: "prepare_ahead", label: "Prepare Ahead Notes", playbookKey: "preparing_ahead" },
  { value: "arriving_tomorrow", label: "Arriving Tomorrow Notes", playbookKey: "arriving_tomorrow" },
  { value: "in_camp", label: "In Camp Notes", playbookKey: "in_camp" },
  { value: "highlights", label: "Highlights", playbookKey: "agent_highlights" },
  { value: "guest_feedback", label: "Guest Feedback", playbookKey: "client_feedback" },
] satisfies readonly {
  value: StayHandoverCategory
  label: string
  playbookKey: StayPlaybookTemplateKey
}[]

const finalCategory = {
  value: "final_camp_handover",
  label: "Final Handover Notes",
  playbookKey: "departing_soon",
} satisfies {
  value: StayHandoverCategory
  label: string
  playbookKey: StayPlaybookTemplateKey
}

export function StayNotes({
  finalNotes,
  notes,
  playbookTemplates,
  stayId,
}: StayNotesProps) {
  const [capturedFinalNotes, setCapturedFinalNotes] = useState([...finalNotes])
  const [capturedNotes, setCapturedNotes] = useState([...notes])
  const [mode, setMode] = useState<ModalMode | null>(null)
  const [category, setCategory] = useState<StayHandoverCategory>("prepare_ahead")
  const [body, setBody] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [notePendingRemoval, setNotePendingRemoval] = useState<StayHandoverNote | null>(null)
  const [saving, setSaving] = useState(false)
  const groupedNotes = groupNotes(capturedNotes)
  const selectedOption = mode === "final" ? finalCategory : mode === "stay" ? noteCategoryOptions.find((option) => option.value === category)! : null

  function openModal(nextMode: ModalMode) {
    setMode(nextMode)
    setError(null)
    setBody("")
    if (nextMode === "stay") setCategory("prepare_ahead")
  }

  async function addNote() {
    if (!mode || saving) return

    setSaving(true)
    setError(null)

    const noteCategory = mode === "final" ? finalCategory.value : category

    try {
      const response = await post(`/api/stays/${stayId}/handover_notes`, {
        body: {
          stay_handover_note: {
            body,
            category: noteCategory,
          },
        },
        responseKind: "json",
      })

      const note = await response.json as StayHandoverNote
      if (note.category === finalCategory.value) {
        setCapturedFinalNotes((currentNotes) => [...currentNotes, note])
      } else {
        setCapturedNotes((currentNotes) => [...currentNotes, note])
      }
      setMode(null)
      setBody("")
    } catch {
      setError("Note could not be added.")
    } finally {
      setSaving(false)
    }
  }

  function openRemoveConfirmation(note: StayHandoverNote) {
    setNotePendingRemoval(note)
  }

  function closeRemoveConfirmation() {
    setNotePendingRemoval(null)
  }

  async function removeNote(note: StayHandoverNote) {
    await destroy(`/api/stays/${stayId}/handover_notes/${note.id}`)
    if (note.category === finalCategory.value) {
      setCapturedFinalNotes((currentNotes) => currentNotes.filter((currentNote) => currentNote.id !== note.id))
    } else {
      setCapturedNotes((currentNotes) => currentNotes.filter((currentNote) => currentNote.id !== note.id))
    }
  }

  return (
    <>
      <article className="stay-card stay-card--compact">
        <header className="stay-card__header">
          <h3>Final handover notes</h3>
          <button className="stay-button stay-button--primary" onClick={() => openModal("final")} type="button">
            <span aria-hidden="true" className="stay-button__icon" />
            Add
          </button>
        </header>
        <div className="stay-note-list">
          {capturedFinalNotes.length ? (
            capturedFinalNotes.map((note) => <StayNote key={note.id} note={note} onRemoveNote={openRemoveConfirmation} />)
          ) : (
            <p className="stay-empty-box">No notes captured yet.</p>
          )}
        </div>
      </article>

      <h2 className="stay-section-heading">Stay notes</h2>
      <article className="stay-card">
        <header className="stay-card__header">
          <h3>Notes for this stay</h3>
          <button className="stay-button stay-button--primary" onClick={() => openModal("stay")} type="button">
            <span aria-hidden="true" className="stay-button__icon" />
            Add
          </button>
        </header>
        <div className="stay-notes-body">
          {capturedNotes.length ? (
            noteCategoryOptions.map((option) => {
              const categoryNotes = groupedNotes[option.value] ?? []
              if (!categoryNotes.length) return null

              return (
                <section key={option.value}>
                  <h4 className="stay-notes-category-title">{option.label}</h4>
                  <div className="stay-note-list">
                    {categoryNotes.map((note) => <StayNote key={note.id} note={note} onRemoveNote={openRemoveConfirmation} />)}
                  </div>
                </section>
              )
            })
          ) : (
            <p className="stay-empty-box">No notes captured yet.</p>
          )}
        </div>
      </article>

      {selectedOption ? (
        <Modal
          footer={
            <>
              <button
                className="stay-button stay-button--secondary"
                disabled={saving}
                onClick={() => setMode(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="stay-button stay-button--primary"
                disabled={saving || !body.trim()}
                onClick={() => void addNote()}
                type="button"
              >
                {saving ? "Adding" : "Add"}
              </button>
            </>
          }
          onClose={() => setMode(null)}
          open
          title={mode === "final" ? "Add final handover notes" : "Add notes for stay"}
        >
          {error ? <p className="stay-error">{error}</p> : null}
          {mode === "stay" ? (
            <div className="stay-form-field">
              <label htmlFor="stay_note_category">Category</label>
              <select
                id="stay_note_category"
                onChange={(event) => setCategory(event.target.value as StayHandoverCategory)}
                value={category}
              >
                {noteCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {playbookTemplates[selectedOption.playbookKey] ? (
            <aside className="stay-playbook-guidance">
              <h4>{selectedOption.label} - Playbook guidance</h4>
              <p>{playbookTemplates[selectedOption.playbookKey]}</p>
            </aside>
          ) : null}
          <div className="stay-form-field">
            <label htmlFor="stay_note_body">Note</label>
            <textarea
              id="stay_note_body"
              onChange={(event) => setBody(event.target.value)}
              required
              value={body}
            />
          </div>
        </Modal>
      ) : null}

      {notePendingRemoval ? (
        <ConfirmDeleteModal
          errorMessage="Note could not be removed."
          message="Are you sure you want to delete this note?"
          onClose={closeRemoveConfirmation}
          onConfirm={() => removeNote(notePendingRemoval)}
          title="Delete handover note"
        />
      ) : null}
    </>
  )
}

function StayNote({
  note,
  onRemoveNote,
}: {
  note: StayHandoverNote
  onRemoveNote: (note: StayHandoverNote) => void
}) {
  return (
    <article className="stay-note">
      <div className="stay-note__content">
        <p>{note.body}</p>
        <button
          aria-label={`Remove note: ${note.body}`}
          className="stay-note__remove"
          onClick={() => onRemoveNote(note)}
          type="button"
        >
          <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
            <path d="M5.5 2.75h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
            <path d="M3.25 5h9.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
            <path d="M5 5.25l.45 7.25h5.1L11 5.25" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" />
          </svg>
        </button>
      </div>
      <footer>Captured by {note.created_by} · {note.created_at}</footer>
    </article>
  )
}

function groupNotes(notes: readonly StayHandoverNote[]) {
  return notes.reduce<Partial<Record<StayHandoverCategory, StayHandoverNote[]>>>((memo, note) => {
    const categoryNotes = memo[note.category] || []
    categoryNotes.push(note)
    memo[note.category] = categoryNotes
    return memo
  }, {})
}
